import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';
import { getCategoryBySlug } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');
  const relatedArticles = article ? getRelatedArticles(article) : [];
  const category = article ? getCategoryBySlug(article.categorySlug) : undefined;

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <i className="ri-file-unknow-line text-foreground-400 text-4xl"></i>
            </div>
            <h2 className="font-heading text-xl font-bold text-foreground-900 mb-2">文章未找到</h2>
            <p className="text-foreground-600 text-sm mb-6">该文章不存在或已被删除</p>
            <Link
              to="/"
              className="whitespace-nowrap cursor-pointer inline-block px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    datePublished: article.publishedAt,
    author: {
      '@type': 'Organization',
      name: '51fun吃瓜黑料不打烊',
    },
    publisher: {
      '@type': 'Organization',
      name: '51fun吃瓜黑料不打烊',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `http://oheaven.cn/article/${article.slug}`,
    },
    image: article.coverImage,
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '51fun吃瓜黑料不打烊的内容来源是什么？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '51fun吃瓜黑料不打烊的内容来源于网友投稿、公开媒体报道及网络信息聚合，所有内容均标注信息来源，仅供娱乐阅读参考。',
        },
      },
      {
        '@type': 'Question',
        name: '如何在51fun上爆料吃瓜？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过本站的"订阅爆料"功能或发送邮件至tips@oheaven.cn进行匿名爆料，我们严格保护爆料人隐私。',
        },
      },
    ],
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'http://oheaven.cn/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.category,
        item: `http://oheaven.cn/category/${article.categorySlug}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>

      <Navbar />
      <main className="min-h-screen">
        {/* Hero */}
        <section className="relative w-full h-[300px] md:h-[420px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
          <div className="relative z-10 w-full h-full flex flex-col justify-end px-4 md:px-6 pb-10 md:pb-16">
            <div className="max-w-4xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-500/20 border border-accent-500/30 mb-4">
                <span className="text-accent-300 text-xs whitespace-nowrap">{article.category}</span>
              </div>
              <h1 className="font-heading text-xl md:text-3xl lg:text-4xl font-bold text-background-50 mb-3 leading-tight">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-background-50/60 text-xs">
                <span>{article.publishedAt}</span>
                <span className="flex items-center gap-1">
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-eye-line text-[10px]"></i>
                  </div>
                  {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views} 阅读
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-chat-3-line text-[10px]"></i>
                  </div>
                  {article.comments} 评论
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Breadcrumb */}
        <div className="px-4 md:px-6 max-w-4xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: article.category, href: `/category/${article.categorySlug}` },
              { label: article.title },
            ]}
          />
        </div>

        {/* Article Content */}
        <article className="py-8 md:py-12 bg-background-50">
          <div className="px-4 md:px-6 max-w-4xl mx-auto">
            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-8">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>

            {/* Content */}
            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Share */}
            <div className="mt-12 pt-8 border-t border-background-200">
              <div className="flex items-center gap-4">
                <span className="text-foreground-600 text-sm">分享到：</span>
                <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-accent-100 hover:text-accent-600 transition-colors cursor-pointer">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-weibo-line"></i>
                  </div>
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-accent-100 hover:text-accent-600 transition-colors cursor-pointer">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-wechat-line"></i>
                  </div>
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-accent-100 hover:text-accent-600 transition-colors cursor-pointer">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-link"></i>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles - Testimonial-style dark section */}
        {relatedArticles.length > 0 && (
          <section className="py-16 md:py-20 bg-secondary-950 rounded-t-3xl mx-4 md:mx-6 relative overflow-hidden">
            {/* Decorative */}
            <div className="absolute top-6 left-8 opacity-20">
              <svg width="60" height="80" viewBox="0 0 60 80" fill="none">
                <path d="M10 70 Q30 10 50 40" stroke="currentColor" strokeWidth="1" className="text-foreground-600" fill="none" />
                <path d="M5 65 Q25 15 55 35" stroke="currentColor" strokeWidth="0.5" className="text-foreground-700" fill="none" />
              </svg>
            </div>

            <div className="px-4 md:px-6 max-w-7xl mx-auto relative z-10">
              <div className="mb-10">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-links-line text-accent-400 text-sm"></i>
                  </div>
                  <span className="text-accent-400 text-xs font-label font-semibold tracking-wider uppercase">相关推荐</span>
                </div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-50">更多{article.category}爆料</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.slug}`}
                    className="group bg-secondary-800/50 rounded-lg overflow-hidden hover:bg-secondary-800/80 transition-all cursor-pointer"
                  >
                    <div className="relative h-44 overflow-hidden">
                      <img
                        src={related.coverImage}
                        alt={related.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <h3 className="font-label text-sm font-semibold text-foreground-100 mb-2 line-clamp-2 group-hover:text-accent-400 transition-colors">
                        {related.title}
                      </h3>
                      <span className="text-foreground-500 text-xs">{related.publishedAt}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="px-4 md:px-6 max-w-2xl mx-auto text-center">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-3">
              有<span className="text-accent-600 italic font-heading">猛料</span>要爆？
            </h2>
            <p className="text-foreground-600 text-sm mb-8 leading-relaxed">
              保护爆料人隐私，匿名投稿，让真相曝光！
            </p>
            <a
              href="mailto:hello@oheaven.cn"
              className="whitespace-nowrap cursor-pointer inline-block px-8 py-3 rounded-full bg-secondary-900 text-background-50 text-sm font-medium hover:bg-secondary-800 transition-all"
            >
              投稿爆料
              <div className="w-4 h-4 inline-flex items-center justify-center ml-1">
                <i className="ri-send-plane-line text-xs"></i>
              </div>
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
